import { Container, Divider, lighten, makeStyles, Typography } from '@material-ui/core';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import PageLayout from '../../components/page-layout';
import Section from '../../components/section';
import Seo from '../../components/seo';
import FeatureBlock from './_feature-block';

const useStyles = makeStyles(theme => ({
  featureHeading: {
    marginBottom: theme.spacing(1),
  },
  inlineCode: {
    backgroundColor: `${lighten(theme.palette.secondary.light, 0.1)} !important`,
  },
}));

const ContinuousProvisioningPage: React.FC = () => {
  const classes = useStyles();

  const title = 'Continuous environment provisioning';
  const description = 'End-to-end environments for everyone with the push of a button';

  return (
    <PageLayout>
      <Seo title={title} description={description} />

      <Section variant="hero-green">
        <Typography variant="h1" align="center">
          {title}
        </Typography>
        <Typography variant="subtitle1" align="center">
          {description}
        </Typography>
      </Section>

      <FeatureBlock
        image={
          <StaticImage
            src="../../images/icons/lg/any-cloud-provider.svg"
            alt="Any cloud provider"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Any cloud provider
        </Typography>
        <Typography variant="body1" paragraph>
          Cloud providers and their service offerings are constantly changing and expanding. Asking
          developers to keep up with features from specific vendors only creates vendor lock-in and
          distracts them from developing product features.
        </Typography>
        <Typography variant="body1" paragraph>
          Instead of asking everyone to learn how to deploy to specific providers, Architect
          automatically deploys apps and services to any provider without any configuration changes.
          Your developers stay focused on features, and you'll always be able to swap out providers.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        flipped
        image={
          <StaticImage
            src="../../images/icons/lg/on-demand-environments.svg"
            alt="On-demand environments"
            imgStyle={{ transform: 'scaleX(-1)' }}
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          On-demand environments
        </Typography>
        <Typography variant="body1" paragraph>
          Architect has a built-in dependency resolver for microservices, which means that every
          service is aware of which peer APIs they need to connect to.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect uses this information not only to connect to existing instances of peer
          services, but to provision them as well. This means that even the most complex of
          applications can be deployed into fresh environments with the push of a button.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        image={
          <StaticImage src="../../images/icons/lg/simplified-gitops.svg" alt="Simplified GitOps" />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          GitOps powered previews
        </Typography>
        <Typography variant="body1">
          Architect Components are smart enough to deploy or provision themselves thanks to our
          built-in dependency resolver. That means you can easily use your favorite CI tool, like
          Jenkins, CircleCI, GitHub Actions, and more, to instrument GitOps for all your apps and
          services using our CLI.
        </Typography>
      </FeatureBlock>
    </PageLayout>
  );
};

export default ContinuousProvisioningPage;
